import { Button } from "@/components/ui/button";
import { Home, Wallet, BookOpen, Trophy, Bell, Sparkles, User, LogOut, CreditCard, Settings, ShieldCheck } from "lucide-react";

const NAV_LINKS = [
  { id: "home", label: "Dashboard", mobileLabel: "Dashboard", path: "/dashboard", Icon: Home },
  { id: "portfolio", label: "Portfolio", mobileLabel: "Portfolio", path: "/portfolio", Icon: Wallet },
  { id: "learning", label: "Learning", mobileLabel: "Learning", path: "/learning", Icon: BookOpen },
  { id: "leaderboard", label: "Leaderboard", mobileLabel: "Board", path: "/leaderboard", Icon: Trophy },
  { id: "alerts", label: "Alerts", mobileLabel: null, path: "/alerts", Icon: Bell },
];

export default function DashboardNav({
  navigate,
  isAuthenticated,
  isAdmin,
  hasSubscription,
  predictionCredits,
  logout,
  onSignIn,
  onUpgrade,
  alertBell,
}) {
  return (
    <nav className="relative z-40 border-b border-white/5 backdrop-blur-md sticky top-0">
      <div className="max-w-7xl mx-auto px-3 sm:px-6 py-3 sm:py-4">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex items-center gap-2 sm:gap-6 min-w-0">
            <div
              className="flex items-center gap-2 sm:gap-3 cursor-pointer shrink-0"
              onClick={() => navigate('/')}
            >
              <img src="/logo192.png" alt="TradeInsights logo" className="w-8 h-8 sm:w-10 sm:h-10 rounded-lg" data-testid="navbar-logo" />
              <span className="hidden sm:inline lg:hidden xl:inline text-lg font-bold tracking-tight text-white" style={{ fontFamily: 'Unbounded' }}>
                TRADEINSIGHTS
              </span>
            </div>

            <div className="hidden lg:flex items-center gap-1.5">
              {NAV_LINKS.map(({ id, label, path, Icon }) => (
                <Button
                  key={id}
                  data-testid={`nav-${id}-btn`}
                  variant="ghost"
                  onClick={() => navigate(path)}
                  className="h-10 px-3 text-sm font-semibold text-gray-200 hover:text-white hover:bg-white/10"
                >
                  <Icon className="w-5 h-5 mr-1.5 text-[#00E0FF]/80" />
                  {label}
                </Button>
              ))}
            </div>
          </div>

          <div className="flex items-center gap-2 sm:gap-3 shrink-0">
            {isAuthenticated ? (
              <>
                {/* User Credits/Status */}
                <div className="glass-effect px-2 sm:px-4 py-1.5 sm:py-2 rounded-lg">
                  <div className="flex items-center gap-1.5 sm:gap-2">
                    <Sparkles className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-[#00E0FF]" />
                    <span className="text-xs sm:text-sm text-white font-semibold">
                      {hasSubscription ? "Premium" : `${predictionCredits} Credits`}
                    </span>
                  </div>
                </div>

                {/* Admin badge / link — only renders for users in ADMIN_EMAILS */}
                {isAdmin && (
                  <button
                    data-testid="admin-link-btn"
                    onClick={() => navigate("/admin")}
                    className="flex items-center gap-1.5 px-2 sm:px-3 py-1.5 sm:py-2 rounded-lg bg-[#F59E0B]/15 border border-[#F59E0B]/30 text-[#F59E0B] hover:bg-[#F59E0B]/25 transition-colors"
                    title="Admin dashboard"
                  >
                    <ShieldCheck className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                    <span className="hidden sm:inline text-xs font-bold">ADMIN</span>
                  </button>
                )}

                {/* Alert Bell (AlertDropdown slot) */}
                {alertBell}

                {/* Upgrade Button (if not subscribed) */}
                {!hasSubscription && (
                  <Button
                    data-testid="upgrade-btn"
                    size="sm"
                    onClick={onUpgrade}
                    className="bg-[#00E0FF] text-black hover:bg-[#33E9FF] font-semibold text-xs sm:text-sm px-2 sm:px-4"
                  >
                    <CreditCard className="w-3.5 h-3.5 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
                    <span className="hidden sm:inline">Upgrade</span>
                    <span className="sm:hidden">Pro</span>
                  </Button>
                )}

                {/* User Menu */}
                <Button
                  data-testid="nav-settings-btn"
                  variant="ghost"
                  size="sm"
                  onClick={() => navigate('/settings')}
                  className="text-gray-400 hover:text-white hover:bg-white/5 px-2"
                  title="Settings"
                >
                  <Settings className="w-4 h-4" />
                </Button>
                <Button
                  data-testid="logout-btn"
                  variant="ghost"
                  size="sm"
                  onClick={logout}
                  className="text-gray-400 hover:text-white hover:bg-white/5 px-2 sm:px-3"
                >
                  <LogOut className="w-4 h-4" />
                  <span className="hidden 2xl:inline ml-2">Logout</span>
                </Button>
              </>
            ) : (
              <Button
                data-testid="signin-btn"
                onClick={onSignIn}
                className="bg-[#00E0FF] text-black hover:bg-[#33E9FF] font-semibold text-xs sm:text-sm px-3 sm:px-4"
              >
                <User className="w-4 h-4 mr-1 sm:mr-2" />
                Sign In
              </Button>
            )}

            <div className="hidden xl:flex items-center gap-2">
              <div className="w-2 h-2 rounded-full bg-[#00FFA3] animate-pulse" />
              <span className="text-xs text-gray-500">Live</span>
            </div>
          </div>
        </div>

        {/* Mobile / narrow nav row */}
        <div className="flex lg:hidden items-center gap-1.5 mt-2 overflow-x-auto">
          {NAV_LINKS.filter((l) => l.mobileLabel).map(({ id, mobileLabel, path, Icon }) => (
            <Button
              key={id}
              data-testid={`nav-${id}-btn-mobile`}
              variant="ghost"
              onClick={() => navigate(path)}
              className="h-9 px-3 text-sm font-semibold text-gray-200 hover:text-white hover:bg-white/10 shrink-0"
            >
              <Icon className="w-4 h-4 mr-1.5 text-[#00E0FF]/80" />
              {mobileLabel}
            </Button>
          ))}
        </div>
      </div>
    </nav>
  );
}
